import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { AxiosResponse } from 'axios'
import { INameId } from 'constants/globalTypes'
import { toast } from 'react-toastify'
import {
  IGetAllInventoryFieldSingleResponse,
  IInventoryFieldPostPatchRequest,
} from 'types/api/fieldsApi.interface'
import { IPaginatedResponse } from 'types/api/general.interface'
import { IInvenotryCategoryField, IInventoryFieldsSlice } from 'types/inventory.interface'
import { handleErrors, formatParams, getParams } from 'utils/helpers'
import { baseUrl } from '../../utils/axios'
import { IInventoryFieldsModal } from 'pages/Inventory/Settings/InventoryFieldsModal'

const initialState: IInventoryFieldsSlice = {
  isLoading: false,
  isSingleLoading: false,
  singleField: null,
  totalCount: 0,
  totalPages: 0,
  fields: [],
  noFields: false,
  noSubfields: false,
}

export const getInvSettingsFields = createAsyncThunk(
  'invSettingsFields/getInvSettingsFields',
  async (params: any, thunkAPI) => {
    try {
      params = formatParams(params)
      const resp: AxiosResponse<IPaginatedResponse<IGetAllInventoryFieldSingleResponse>> =
        await baseUrl.get('inventory/field', { params })
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const addInvField = createAsyncThunk(
  'invSettingsFields/addField',
  async (field: IInventoryFieldsModal, thunkAPI) => {
    try {
      const forSubmit: IInventoryFieldPostPatchRequest = {
        label: field.label,
        helpText: field.helpText,
        dataType: field.dataType?.value,
        dropDownItems: field.dropDownItems.map((f) => ({ name: f.name, dataType: f.type.value })),
        unitId: field.unit?.value,
      }
      const resp = await baseUrl.post('inventory/field', forSubmit, thunkAPI)
      await thunkAPI.dispatch(getInvSettingsFields(getParams({ location })))
      return resp.data
    } catch (error) {
      console.log(error)
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const editInvField = createAsyncThunk(
  'invSettingsFields/editField',
  async (field: IInventoryFieldsModal, thunkAPI) => {
    const forSubmit: IInventoryFieldPostPatchRequest = {
      label: field.label,
      helpText: field.helpText,
      dataType: field.dataType.value,
      dropDownItems: field.dropDownItems.map((f) => ({ name: f.name, dataType: f.type.value })),
      unitId: field.unit?.value,
    }

    try {
      const resp = await baseUrl.patch('inventory/field/' + field.id, forSubmit, thunkAPI)
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const getSingleInvField = createAsyncThunk(
  'invSettingsFields/getSingleField',
  async (id: number, thunkAPI) => {
    try {
      const resp: AxiosResponse<IInvenotryCategoryField> = await baseUrl.get(
        'inventory/field/' + id,
      )
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const deleteInvField = createAsyncThunk(
  'invSettingsFields/deleteField',
  async (id: number | string, thunkAPI) => {
    try {
      await baseUrl.delete('inventory/field/' + id)
      await thunkAPI.dispatch(getInvSettingsFields(getParams({ location })))

      return id
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

const inventorySettingsFieldsSlice = createSlice({
  name: 'inventoryItems',
  initialState,
  reducers: {
    clearInventoryField: (state) => {
      state.singleField = null
    },
    clearInventoryFieldsList: (state) => {
      state.totalCount = 0
      state.totalPages = 0
      state.fields = []
    },
  },
  extraReducers: {
    [getInvSettingsFields.pending.type]: (state) => {
      state.isLoading = true
    },
    [getInvSettingsFields.fulfilled.type]: (
      state: IInventoryFieldsSlice,
      { payload }: { payload: IPaginatedResponse<IGetAllInventoryFieldSingleResponse> },
    ) => {
      state.isLoading = false
      state.noFields = payload.data.length < 1
      state.fields = payload.data
      state.totalPages = payload.totalPages
      state.totalCount = payload.totalCount
    },
    [getInvSettingsFields.rejected.type]: (state) => {
      state.isLoading = false
    },
    [getSingleInvField.pending.type]: (state) => {
      state.isSingleLoading = true
    },
    [getSingleInvField.fulfilled.type]: (state, { payload }) => {
      state.isSingleLoading = false
      state.noSubfields = payload.dropDownItems.length < 1
      state.singleField = payload
    },
    [getSingleInvField.rejected.type]: (state) => {
      state.isSingleLoading = false
    },
    [addInvField.pending.type]: (state) => {
      state.isLoading = true
    },
    [addInvField.fulfilled.type]: (state: IInventoryFieldsSlice) => {
      state.isLoading = false
      state.noFields = false
      toast.success('Field added')
    },
    [addInvField.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [editInvField.pending.type]: (state) => {
      state.isLoading = true
    },
    [editInvField.fulfilled.type]: (state, { payload }) => {
      state.singleField = { ...payload, dropDownOptions: payload.dropDownOptions }
      state.noSubfields = payload.dropDownItems?.length < 1 || payload.dataType !== 3
      state.isLoading = false
      toast.success('Field edited')
    },
    [editInvField.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [deleteInvField.fulfilled.type]: (state) => {
      toast.success('Field deleted')
      state.isLoading = false
    },
    [deleteInvField.pending.type]: (state: any) => {
      state.isLoading = true
    },
    [deleteInvField.rejected.type]: (state: any, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
  },
})

export const { clearInventoryField, clearInventoryFieldsList } =
  inventorySettingsFieldsSlice.actions

export default inventorySettingsFieldsSlice.reducer
